import Income from "../../../../model/Income";
import {
  IBudgetCalc,
  IDeductionItemSummary,
  IExpenseCategorySummary,
  IExpenseItemSummary,
  IIncomeItemSummary,
  ITaxItemSummary
} from "../../../../components/calculator/Calculator";
import Expense, {ExpenseCategory} from "../../../../model/Expense";
import Deduction from "../../../../model/Deduction";
import Tax, {TaxType} from "../../../../model/Tax";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from "@mui/material";
import {
  dateToLocalFormat,
  humanizeString,
  numberToMoneyFormat,
  numberToPercentFormat
} from "../../../../stores/StoreUtilities";
import React from "react";
import Scheduled from "../../../../model/Scheduled";
import Model, {BudgetType} from "../../../../model/Model";
import {isNumber} from "../../../../model/ModelUtilities";
import {grey} from "@mui/material/colors";
import User from "../../../../model/User";


const BudgetReport = ({
  user,
  model,
  incomes,
  deductions,
  taxes,
  essentialExpenses,
  discretionaryExpenses,
  generalExpenses,
  housingExpenses,
  loanAndLiabilityExpenses,
  foodAndPersonalCareExpenses,
  insuranceAndMedicalExpenses,
  vehiclesAndTransportationExpenses,
  travelAndEntertainmentExpenses,
  givingAndMiscExpenses,
  budgetCalc,
  budgetType
} : {
  user: User
  model: Model
  incomes: Income[]
  deductions: Deduction[]
  taxes: Tax[]
  essentialExpenses: Expense[]
  discretionaryExpenses: Expense[]
  generalExpenses: Expense[]
  housingExpenses: Expense[]
  loanAndLiabilityExpenses: Expense[]
  foodAndPersonalCareExpenses: Expense[]
  insuranceAndMedicalExpenses: Expense[]
  vehiclesAndTransportationExpenses: Expense[]
  travelAndEntertainmentExpenses: Expense[]
  givingAndMiscExpenses: Expense[]
  budgetCalc: IBudgetCalc
  budgetType: BudgetType
}) => {
  const theme = useTheme()

  const styles = {
    th: {
      fontSize: 12,
      fontWeight: 600,
      lineHeight: 1.1,
    },
    thTitle: {
      fontSize: 12,
      fontWeight: 600,
      lineHeight: 1.1,
      width: 200
    },
    tb: {
      fontSize: 14,
    },
    total: {
      fontSize: 14,
      fontWeight: 700
    },
    totalPos: {
      fontSize: 14,
      fontWeight: 700,
      color: "green"
    },
    totalNeg: {
      fontSize: 14,
      fontWeight: 700,
      color: "red"
    },
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      maxHeight: 15,
      textWrap: "nowrap",
      color: theme.palette.grey["700"]
    }
  }

  const isDetailed = budgetType === BudgetType.Detailed

  const getRepeats = (item: Scheduled) => {
    // switch (item.schedule.frequency) {
    //   case
    // }
    return item.schedule.getDescription(item.startDate)
  }

  const getAnnualInf = (item: any, typename: string) => {
    const annualInf = item["annualInf"]
    return (numberToPercentFormat(isNumber(annualInf) ? annualInf : model.getDefaultInflationRate(typename), 2))
  }

  const getAnnualIncome = (income: Income) => {
    const {id} = income

    let total = 0
    if (budgetCalc) {
      const item = budgetCalc.incomeSummary.byItem.find((item: IIncomeItemSummary) => item.id === id)
      if (item) {
        total = item.total
      }
    }

    return total
  }

  const getAnnualDeduction = (deduction: Deduction) => {
    const {id} = deduction

    let total = 0
    if (budgetCalc) {
      const item = budgetCalc.deductionSummary.byItem.find((item: IDeductionItemSummary) => item.id === id)
      if (item) {
        total = item.total
      }
    }

    return total
  }

  const getAnnualTax = (tax: Tax) => {
    const {id} = tax

    let total = 0
    if (budgetCalc) {
      const item = budgetCalc.taxSummary.byItem.find((item: ITaxItemSummary) => item.id === id)
      if (item) {
        total = item.total
      }
    }

    return total
  }

  const getAnnualExpense = (expense: Expense) => {
    const {id, expenseCategory} = expense

    let total = 0
    if (budgetCalc) {
      const summary = budgetCalc.expenseSummary.byCategory.find((summary: IExpenseCategorySummary) => summary.category === expenseCategory)
      if (summary) {
        const item = summary.byItem.find((item: IExpenseItemSummary) => item.id === id)
        if (item) {
          total = item.total
        }
      }
    }

    return total
  }

  const getAnnualExpenseCategory = (expenseCategory: ExpenseCategory) => {

    let total = 0
    if (budgetCalc) {
      const summary = budgetCalc.expenseSummary.byCategory.find((summary: IExpenseCategorySummary) => summary.category === expenseCategory)
      if (summary) {
        total = summary.total
      }
    }

    return total
  }

  const renderExpenseCategory = (title: string, expenseCategory: ExpenseCategory, expenses: Expense[]) => {
    return (
      <React.Fragment>
        <Box display="flex" flexGrow={1} justifyContent="space-between" alignItems="flex-end" pr={1} mt={2}>
          <Typography variant="h5" color="primary" gutterBottom>{title}</Typography>
        </Box>
        <TableContainer component={Paper}>
          <Table size="small" width="100%">
            <TableHead>
              <TableRow>
                <TableCell sx={styles.thTitle}>Title</TableCell>
                <TableCell sx={styles.th} align="right">Amount</TableCell>
                <TableCell sx={styles.th}>Repeats</TableCell>
                <TableCell sx={styles.th}>Owner</TableCell>
                <TableCell sx={styles.th}>Type</TableCell>
                <TableCell sx={styles.th}>Start Date</TableCell>
                <TableCell sx={styles.th}>End Date</TableCell>
                <TableCell sx={styles.th}>Inflation Rate</TableCell>
                <TableCell sx={styles.th}>Payment Account</TableCell>
                <TableCell sx={styles.th} align="right">Monthly Avg</TableCell>
                <TableCell sx={styles.th} align="right">Annual Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expenses.filter((item: Expense) => !item.archived).map((item: Expense) => {
                const annual = getAnnualExpense(item)
                return <TableRow key={`expense-${item.id}`}>
                  <TableCell sx={styles.tb}>{item.description}</TableCell>
                  <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(item.amount, 0)}</TableCell>
                  <TableCell sx={styles.tb}>{getRepeats(item)}</TableCell>
                  <TableCell sx={styles.tb}>{item.owner?.nickname}</TableCell>
                  <TableCell sx={styles.tb}>{item.discretionary ? "Discretionary" : "Essential"}</TableCell>
                  <TableCell sx={styles.tb}>{dateToLocalFormat(item.startDate)}</TableCell>
                  <TableCell sx={styles.tb}>{dateToLocalFormat(item.endDate)}</TableCell>
                  <TableCell sx={styles.tb}>{getAnnualInf(item, "expense")}</TableCell>
                  <TableCell sx={styles.tb}>{item.asset?.description}</TableCell>
                  <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(Math.round(annual / 12), 0)}</TableCell>
                  <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(annual, 0)}</TableCell>
                </TableRow>
              })}
              <TableRow>
                <TableCell sx={styles.total}>Totals</TableCell>
                <TableCell sx={styles.totalPos} colSpan={9} align="right">{numberToMoneyFormat(Math.round(getAnnualExpenseCategory(expenseCategory) / 12), 0)}</TableCell>
                <TableCell sx={styles.totalPos} align="right">{numberToMoneyFormat(getAnnualExpenseCategory(expenseCategory), 0)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </React.Fragment>
    )
  }

  const renderExpenseType = (title: string, discretionary: boolean, expenses: Expense[]) => {
    const annualTotal = discretionary ? budgetCalc.totalDiscretionaryExpenses : budgetCalc.totalEssentialExpenses

    return (
      <React.Fragment>
        <Box display="flex" flexGrow={1} justifyContent="space-between" alignItems="flex-end" pr={1} mt={2}>
          <Typography variant="h5" color="primary" gutterBottom>{title}</Typography>
        </Box>
        <TableContainer component={Paper}>
          <Table size="small" width="100%">
            <TableHead>
              <TableRow>
                <TableCell sx={styles.thTitle}>Title</TableCell>
                <TableCell sx={styles.th} align="right">Amount</TableCell>
                <TableCell sx={styles.th}>Repeats</TableCell>
                <TableCell sx={styles.th}>Owner</TableCell>
                <TableCell sx={styles.th}>Start Date</TableCell>
                <TableCell sx={styles.th}>End Date</TableCell>
                <TableCell sx={styles.th}>Inflation Rate</TableCell>
                <TableCell sx={styles.th}>Payment Account</TableCell>
                <TableCell sx={styles.th} align="right">Monthly Avg</TableCell>
                <TableCell sx={styles.th} align="right">Annual Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expenses.filter((item: Expense) => !item.archived).map((item: Expense) => {
                const annual = getAnnualExpense(item)
                return <TableRow key={`expense-${item.id}`}>
                  <TableCell sx={styles.tb}>{item.description}</TableCell>
                  <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(item.amount, 0)}</TableCell>
                  <TableCell sx={styles.tb}>{getRepeats(item)}</TableCell>
                  <TableCell sx={styles.tb}>{item.owner?.nickname}</TableCell>
                  <TableCell sx={styles.tb}>{dateToLocalFormat(item.startDate)}</TableCell>
                  <TableCell sx={styles.tb}>{dateToLocalFormat(item.endDate)}</TableCell>
                  <TableCell sx={styles.tb}>{getAnnualInf(item, "expense")}</TableCell>
                  <TableCell sx={styles.tb}>{item.asset?.description}</TableCell>
                  <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(Math.round(annual / 12), 0)}</TableCell>
                  <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(annual, 0)}</TableCell>
                </TableRow>
              })}
              <TableRow>
                <TableCell sx={styles.total}>Totals</TableCell>
                <TableCell sx={styles.totalPos} colSpan={9} align="right">{numberToMoneyFormat(Math.round(annualTotal / 12), 0)}</TableCell>
                <TableCell sx={styles.totalPos} align="right">{numberToMoneyFormat(annualTotal, 0)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </React.Fragment>
    )
  }

  const renderNetIncome = (monthly: boolean = false) => {
    let netIncome = 0
    if (budgetCalc) {
      netIncome = budgetCalc.totalIncome - budgetCalc.totalDeductions - budgetCalc.totalExpenses
      if (budgetCalc.totalEstimatedTaxes) {
        netIncome -= budgetCalc.totalEstimatedTaxes
      } else {
        netIncome -= budgetCalc.totalTaxes
      }
      if (monthly) {
        netIncome = Math.round(netIncome / 12)
      }
    }

    const color = (netIncome < 0) ? "red" : "green"
    return (
      <Typography variant="h4" color={color}>{numberToMoneyFormat(netIncome, 0)}</Typography>
    )
  }

  return (
    <Box>
      <Box display="flex" flexGrow={1} justifyContent="space-between" alignItems="flex-end" pr={1} mt={1}>
        <Typography variant="h5" color="primary" gutterBottom>Income</Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small" width="100%">
          <TableHead>
            <TableRow>
              <TableCell sx={styles.thTitle}>Title</TableCell>
              <TableCell sx={styles.th} align="right">Amount</TableCell>
              <TableCell sx={styles.th}>Repeats</TableCell>
              <TableCell sx={styles.th}>Owner</TableCell>
              <TableCell sx={styles.th}>Income Type</TableCell>
              <TableCell sx={styles.th}>Start Date</TableCell>
              <TableCell sx={styles.th}>End Date</TableCell>
              <TableCell sx={styles.th}>Inflation Rate</TableCell>
              <TableCell sx={styles.th} align="right">Monthly Avg</TableCell>
              <TableCell sx={styles.th} align="right">Annual Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {incomes.filter((item: Income) => !item.archived).map((item: Income) => {
              const annual = getAnnualIncome(item)
              return <TableRow key={`income-${item.id}`}>
                <TableCell sx={styles.tb}>{item.description}</TableCell>
                <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(item.amount, 0)}</TableCell>
                <TableCell sx={styles.tb}>{getRepeats(item)}</TableCell>
                <TableCell sx={styles.tb}>{item.owner?.nickname}</TableCell>
                <TableCell sx={styles.tb}>{item.incomeTypeDef.label}</TableCell>
                <TableCell sx={styles.tb}>{dateToLocalFormat(item.startDate)}</TableCell>
                <TableCell sx={styles.tb}>{dateToLocalFormat(item.endDate)}</TableCell>
                <TableCell sx={styles.tb}>{getAnnualInf(item, "income")}</TableCell>
                <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(Math.round(annual / 12), 0)}</TableCell>
                <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(annual, 0)}</TableCell>
              </TableRow>
            })}
            <TableRow>
              <TableCell sx={styles.total}>Totals</TableCell>
              <TableCell sx={styles.totalPos} colSpan={8} align="right">{numberToMoneyFormat(Math.round(budgetCalc.totalIncome / 12), 0)}</TableCell>
              <TableCell sx={styles.totalPos} align="right">{numberToMoneyFormat(budgetCalc.totalIncome, 0)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" flexGrow={1} justifyContent="space-between" alignItems="flex-end" pr={1} mt={2}>
        <Typography variant="h5" color="primary" gutterBottom>Deductions & Contributions</Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small" width="100%">
          <TableHead>
            <TableRow>
              <TableCell sx={styles.thTitle}>Title</TableCell>
              <TableCell sx={styles.th} align="right">Amount</TableCell>
              <TableCell sx={styles.th}>Repeats</TableCell>
              <TableCell sx={styles.th}>Contribution Account</TableCell>
              <TableCell sx={styles.th}>Start Date</TableCell>
              <TableCell sx={styles.th}>End Date</TableCell>
              <TableCell sx={styles.th}>Inflation Rate</TableCell>
              <TableCell sx={styles.th} align="right">Monthly Avg</TableCell>
              <TableCell sx={styles.th} align="right">Annual Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deductions.filter((item: Deduction) => !item.archived).map((item: Deduction) => {
              const annual = getAnnualDeduction(item)
              return <TableRow key={`deduction-${item.id}`}>
                <TableCell sx={styles.tb}>{item.description}</TableCell>
                <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(item.amount, 0)}</TableCell>
                <TableCell sx={styles.tb}>{getRepeats(item)}</TableCell>
                <TableCell sx={styles.tb}>{item.asset?.description}</TableCell>
                <TableCell sx={styles.tb}>{dateToLocalFormat(item.startDate)}</TableCell>
                <TableCell sx={styles.tb}>{dateToLocalFormat(item.endDate)}</TableCell>
                <TableCell sx={styles.tb}>{getAnnualInf(item, "deduction")}</TableCell>
                <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(Math.round(annual / 12), 0)}</TableCell>
                <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(annual, 0)}</TableCell>
              </TableRow>
            })}
            <TableRow>
              <TableCell sx={styles.total}>Totals</TableCell>
              <TableCell sx={styles.totalPos} colSpan={7}  align="right">{numberToMoneyFormat(Math.round(budgetCalc.totalDeductions / 12), 0)}</TableCell>
              <TableCell sx={styles.totalPos}align="right">{numberToMoneyFormat(budgetCalc.totalDeductions, 0)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" flexGrow={1} justifyContent="space-between" alignItems="flex-end" pr={1} mt={2}>
        <Typography variant="h5" color="primary" gutterBottom>Tax Withholdings</Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small" width="100%">
          <TableHead>
            <TableRow>
              <TableCell sx={styles.thTitle}>Title</TableCell>
              <TableCell sx={styles.th} align="right">Amount</TableCell>
              <TableCell sx={styles.th}>Repeats</TableCell>
              <TableCell sx={styles.th}>Owner</TableCell>
              <TableCell sx={styles.th}>Tax Type</TableCell>
              <TableCell sx={styles.th}>Start Date</TableCell>
              <TableCell sx={styles.th}>End Date</TableCell>
              <TableCell sx={styles.th}>Inflation Rate</TableCell>
              <TableCell sx={styles.th} align="right">Monthly Avg</TableCell>
              <TableCell sx={styles.th} align="right">Annual Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {taxes.filter((item: Tax) => !item.archived).map((item: Tax) => {
              const annual = getAnnualTax(item)
              return <TableRow key={`tax-${item.id}`}>
                <TableCell sx={styles.tb}>{item.description}</TableCell>
                <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(item.amount, 0)}</TableCell>
                <TableCell sx={styles.tb}>{getRepeats(item)}</TableCell>
                <TableCell sx={styles.tb}>{item.owner?.nickname}</TableCell>
                <TableCell sx={styles.tb}>{humanizeString(TaxType[item.taxType])}</TableCell>
                <TableCell sx={styles.tb}>{dateToLocalFormat(item.startDate)}</TableCell>
                <TableCell sx={styles.tb}>{dateToLocalFormat(item.endDate)}</TableCell>
                <TableCell sx={styles.tb}>{getAnnualInf(item, "tax")}</TableCell>
                <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(Math.round(annual / 12), 0)}</TableCell>
                <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(annual, 0)}</TableCell>
              </TableRow>
            })}
            <TableRow>
              <TableCell sx={styles.total}>Totals</TableCell>
              <TableCell sx={styles.totalPos} colSpan={8} align="right">{numberToMoneyFormat(Math.round(budgetCalc.totalTaxes / 12), 0)}</TableCell>
              <TableCell sx={styles.totalPos} align="right">{numberToMoneyFormat(budgetCalc.totalTaxes, 0)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {!isDetailed && renderExpenseType("Essential Expenses", false, essentialExpenses)}
      {!isDetailed && renderExpenseType("Discretionary Expenses", true, discretionaryExpenses)}

      {isDetailed && renderExpenseCategory("General Expenses", ExpenseCategory.General, generalExpenses)}
      {isDetailed && renderExpenseCategory("Housing Expenses", ExpenseCategory.Housing, housingExpenses)}
      {isDetailed && renderExpenseCategory("Loan & Liability Expenses", ExpenseCategory.LoansAndLiabilities, loanAndLiabilityExpenses)}
      {isDetailed && renderExpenseCategory("Food & Personal Care Expenses", ExpenseCategory.FoodAndPersonalCare, foodAndPersonalCareExpenses)}
      {isDetailed && renderExpenseCategory("Insurance & Medical Expenses", ExpenseCategory.InsuranceAndMedical, insuranceAndMedicalExpenses)}
      {isDetailed && renderExpenseCategory("Vehicle & Transportation Expenses", ExpenseCategory.VehiclesAndTransportation, vehiclesAndTransportationExpenses)}
      {isDetailed && renderExpenseCategory("Travel & Entertainment Expenses", ExpenseCategory.TravelAndEntertainment, travelAndEntertainmentExpenses)}
      {isDetailed && renderExpenseCategory("Giving & Misc Expenses", ExpenseCategory.GivingAndMisc, givingAndMiscExpenses)}

      <TableContainer component={Paper} sx={{mt:2, bgcolor:grey["200"]}}>
        <Table size="small" width="100%">
          <TableHead>
            <TableCell sx={styles.th}></TableCell>
            <TableCell sx={styles.th} align="right">Monthly Avg</TableCell>
            <TableCell sx={styles.th} align="right">Annual Amount</TableCell>
          </TableHead>
          <TableBody>
            <TableCell><Typography variant="h4" color="primary">Budget Surplus / Shortfall</Typography></TableCell>
            <TableCell sx={styles.totalPos} align="right">{renderNetIncome(true)}</TableCell>
            <TableCell sx={styles.totalPos} align="right">{renderNetIncome()}</TableCell>
          </TableBody>
        </Table>
      </TableContainer>

    </Box>
  )
}

export default BudgetReport